import { RoundedButton } from '@components/ButtonsAlt/RoundedButton'
import { EmptyView } from '@components/Fillers/EmptyView'
import { useGeo } from '@features/geo/use-geo'
import { HomeScreenHeader } from '@features/home/components/HomeScreenHeader'
import { NeedsListView } from '@features/needs/components/NeedsListView'
import { useGeoNeedsQuery } from '@features/needs/infinite/lib/useGeoNeedsQuery'
import { useNeedsInfiniteQuery } from '@features/needs/infinite/useNeedsInfiniteQuery'
import { needKeysNext } from '@features/needs/model/query-keys'
import { useUserOrgs } from '@features/organizations/hooks/useUserOrgs'
import type { Geo } from '@purposity/sdk/schemas'
import * as Colors from '@themes/Colors'
import React, { useCallback, useMemo } from 'react'
import { Platform, Pressable, StyleSheet, View } from 'react-native'
import { SceneMap, SceneRendererProps } from 'react-native-tab-view'
import { useRouter } from 'solito/router'
import { SceneTypes } from '~/Components/Tabs'
import { TabLabel } from '~/Components/Tabs/TabLabel'

type AltRenderScene = (
  props: Pick<SceneRendererProps, 'jumpTo'> & {
    route: any
  }
) => JSX.Element

export function HomeScreen(props?: Geo) {
  const [routes] = React.useState([
    { key: 'home', title: 'Home', sceneType: SceneTypes.FlatList },
    { key: 'following', title: 'Following', sceneType: SceneTypes.FlatList },
  ] as const)

  const [currentIndex, setIndexTo] = React.useState(0)

  const jumpTo = useCallback(
    (key: string) => setIndexTo(routes.findIndex((route) => route.key === key)),
    [routes]
  )

  const RenderScene = useMemo(
    () =>
      SceneMap({
        [routes[0].key]: HomeRoute,
        [routes[1].key]: FollowingRoute,
      }),
    [routes]
  ) as AltRenderScene

  const handleTabPress = useCallback(
    (nextIndex: number) => {
      if (Platform.OS === 'web') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
      setIndexTo(nextIndex)
    },
    [setIndexTo]
  )

  return (
    <View style={{ borderWidth: 1, borderColor: '#E0E0E0' }}>
      <View>
        <HomeScreenHeader />
      </View>
      <View
        style={{ height: 1, width: '100%', backgroundColor: Colors.borderGray }}
      />
      <View
        style={{
          height: 40,
          alignItems: 'stretch',
          justifyContent: 'space-around',
          flexDirection: 'row',
          backgroundColor: Colors.white,
          position: 'sticky',
          top: 0,
          zIndex: 999,
        }}
      >
        {routes.map((route, i) => (
          <Pressable
            key={route.key}
            style={{
              flexGrow: 1,
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              borderBottomWidth: currentIndex === i ? 2 : 2,
              borderBottomColor:
                currentIndex === i ? Colors.primary : Colors.white,
            }}
            onPress={() => handleTabPress(i)}
          >
            <TabLabel route={route} focused={currentIndex === i} />
          </Pressable>
        ))}
      </View>

      <RenderScene route={routes[currentIndex]} jumpTo={jumpTo} />
    </View>
  )
}

function HomeRoute() {
  const geoEnvQuery = useGeo()

  const isGeoEnabled = useMemo(() => {
    // check dependencies
    if (!geoEnvQuery) {
      return false
    }
    return true
  }, [geoEnvQuery])

  // Query for urgent needs - always enabled
  const urgentQuery = useNeedsInfiniteQuery({
    ...needKeysNext.urgent(),
    enabled: true,
  })

  const geoQuery = useGeoNeedsQuery({ enabled: isGeoEnabled, forHome: true })

  const isFallbackEnabled = useMemo(() => {
    // check dependencies
    // check previous query
    const flags = {
      allPredsAreDisabled: !isGeoEnabled,
      predIsDone: geoQuery.isSuccess && !geoQuery.hasNextPage,
    }
    return Object.values(flags).some((item) => item)
  }, [geoQuery, isGeoEnabled])

  const fallbackQuery = useNeedsInfiniteQuery({
    ...needKeysNext.fallback(),
    enabled: isFallbackEnabled,
  })

  return (
    <View style={{ flex: 1 }}>
      <View style={{ padding: 12 }}>
        {/* Urgent Needs Section */}
        {urgentQuery.data?.pages &&
          urgentQuery.data.pages[0]?.needs_connection?.edges?.length > 0 && (
            <View style={{ marginBottom: 16 }}>
              <NeedsListView
                isLoading={urgentQuery.isLoading}
                isError={urgentQuery.isError}
                pages={urgentQuery.data?.pages}
                hasNextPage={false} // Don't enable pagination for urgent needs
                fetchNextPage={urgentQuery.fetchNextPage}
                isFetchingNextPage={urgentQuery.isFetchingNextPage}
                itemProps={{
                  containerProps: {
                    style: {
                      marginBottom: 16,
                    },
                  },
                }}
              />
            </View>
          )}

        {/* Regular Needs Section (Geo or Fallback) */}
        <View>
          {isGeoEnabled && (
            <NeedsListView
              isLoading={geoQuery.isLoading}
              isError={geoQuery.isError}
              pages={geoQuery.data?.pages}
              hasNextPage={geoQuery.hasNextPage}
              fetchNextPage={geoQuery.fetchNextPage}
              isFetchingNextPage={geoQuery.isFetchingNextPage}
              itemProps={{
                containerProps: {
                  style: {
                    marginBottom: 16,
                  },
                },
              }}
            />
          )}
          {isFallbackEnabled && (
            <NeedsListView
              isLoading={fallbackQuery.isLoading}
              isError={fallbackQuery.isError}
              pages={fallbackQuery.data?.pages}
              hasNextPage={fallbackQuery.hasNextPage}
              fetchNextPage={fallbackQuery.fetchNextPage}
              isFetchingNextPage={fallbackQuery.isFetchingNextPage}
              itemProps={{
                containerProps: {
                  style: {
                    marginBottom: 16,
                  },
                },
              }}
            />
          )}
        </View>
      </View>
    </View>
  )
}

function FollowingRoute() {
  const userOrgsQuery = useUserOrgs()
  const router = useRouter()

  const isFollowingEnabled = useMemo(() => {
    return userOrgsQuery.data && userOrgsQuery.data.length > 0
  }, [userOrgsQuery.data])

  const followingQuery = useNeedsInfiniteQuery({
    ...needKeysNext.orgs(userOrgsQuery.data?.map((i) => i.id) || []),
    enabled: isFollowingEnabled,
  })

  return isFollowingEnabled ? (
    <View style={{ flex: 1 }}>
      <NeedsListView
        isLoading={followingQuery.isLoading}
        isError={followingQuery.isError}
        pages={followingQuery.data?.pages}
        hasNextPage={followingQuery.hasNextPage}
        fetchNextPage={followingQuery.fetchNextPage}
        isFetchingNextPage={followingQuery.isFetchingNextPage}
        containerProps={{
          style: styles.routeContainer,
        }}
        itemProps={{
          containerProps: {
            style: {
              marginBottom: 16,
            },
          },
        }}
      />
    </View>
  ) : (
    <EmptyView
      message="You don't follow any organizations yet."
      actions={
        <RoundedButton
          text="Search"
          onPress={() => router.push('/organizations')}
          backgroundColor={Colors.primary}
          textColor={Colors.white}
        />
      }
    />
  )
}

const styles = StyleSheet.create({
  routeContainer: {
    flexGrow: 1,
    flexShrink: 0,
    padding: 12,
    paddingTop: 16,
    backgroundColor: Colors.white,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.black,
  },
})
